// Split from theme.js: global custom elements (lines 1076-2275, 2305-2613, 2839-3075, 3077-3557, 3559-3852, 3962-4144, 4320-4697, 6312-6325)
class LoadingBar extends HTMLElement {
  constructor() {
    super()

    document.addEventListener('page:loaded', () => {
      Motion.animate(this, { opacity: 0 }, { duration: 1 }).finished.then(() => {
        this.hidden = true
      })
    })
  }
}
customElements.define('loading-bar', LoadingBar)

class MouseCursor extends HTMLElement {
  constructor() {
    super()

    if (theme.config.isTouch) return

    this.config = {
      posX: 0,
      posY: 0
    }

    document.addEventListener('mousemove', (event) => {
      this.config.posX += (event.clientX - this.config.posX) / 4
      this.config.posY += (event.clientY - this.config.posY) / 4

      this.style.setProperty('--x', `${this.config.posX}px`)
      this.style.setProperty('--y', `${this.config.posY}px`)
    })
  }
}
customElements.define('mouse-cursor', MouseCursor)

class CustomHeader extends HTMLElement {
  constructor() {
    super()
  }

  get allowTransparent() {
    if (document.querySelector('.shopify-section:first-child [allow-transparent-header]')) {
      return true
    }

    return false
  }

  get headerSection() {
    return document.querySelector('.header-section')
  }

  connectedCallback() {
    this.init()
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this)
    }

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section')
      section.addEventListener('shopify:section:load', this.init.bind(this))
      section.addEventListener('shopify:section:unload', this.init.bind(this))
      section.addEventListener('shopify:section:reorder', this.init.bind(this))
    }
  }

  init() {
    this.setHeight()

    if (this.allowTransparent) {
      this.headerSection.classList.add('header-transparent')
      this.headerSection.classList.add('no-animate')

      setTimeout(() => {
        this.headerSection.classList.remove('no-animate')
      }, 500)
    } else {
      this.headerSection.classList.remove('header-transparent')
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--header-height', this.getBoundingClientRect().height.toFixed(1) + 'px')

      if (this.classList.contains('header--center') && document.documentElement.style.getPropertyValue('--header-nav-height').length === 0) {
        document.documentElement.style.setProperty('--header-nav-height', Math.round(document.getElementById('MenuToggle')?.clientHeight) + 'px')
      }
    })
  }
}
customElements.define('custom-header', CustomHeader, { extends: 'header' })

class StickyHeader extends CustomHeader {
  constructor() {
    super()
    this.isScrolled = false // Track scroll state to prevent class toggling
    this.buffer = 10 // Pixel buffer to create smooth transition zone
  }

  get isAlwaysSticky() {
    return this.getAttribute('data-sticky-type') === 'always'
  }

  connectedCallback() {
    super.connectedCallback()

    // Initialize scroll tracking values
    this.currentScrollTop = 0
    this.firstScrollTop = window.scrollY
    this.initialHeaderHeight = this.getBoundingClientRect().height // Cache initial height
    this.headerBounds = this.headerSection.getBoundingClientRect()

    this.beforeInit()
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky')
    this.headerSection.setAttribute('data-sticky-type', this.getAttribute('data-sticky-type'))

    if (this.isAlwaysSticky) {
      this.headerSection.classList.add('header-sticky')
    }

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), false)
  }

  onScrollHandler() {
    const scrollTop = window.scrollY

    // Calculate scroll thresholds based on initial header height (prevents changing thresholds)
    const scrollThreshold = this.headerBounds.top + this.firstScrollTop + this.initialHeaderHeight
    const scrollThresholdNav = this.headerBounds.top + this.firstScrollTop + this.initialHeaderHeight * 2

    // Hysteresis: Different thresholds for scrolling down vs up prevents rapid toggling
    const scrollDownThreshold = scrollThreshold + this.buffer
    const scrollUpThreshold = scrollThreshold - this.buffer

    // Toggle 'header-scrolled' class with buffer zone
    if (!this.isScrolled && scrollTop > scrollDownThreshold) {
      // Scrolling down past threshold
      this.isScrolled = true
      this.headerSection.classList.add('header-scrolled')
      document.documentElement.style.setProperty('--sticky-header-height', this.getBoundingClientRect().height.toFixed(1) + 'px')
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: true } }))
    } else if (this.isScrolled && scrollTop < scrollUpThreshold) {
      // Scrolling up past threshold
      this.isScrolled = false
      this.headerSection.classList.remove('header-scrolled')
      this.headerSection.classList.remove('header-nav-scrolled')
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: false } }))
    }

    // Add 'header-nav-scrolled' when scrolled past 2x header height
    if (this.isScrolled && scrollTop > scrollThresholdNav) {
      this.headerSection.classList.add('header-nav-scrolled')

      // Close any open dropdown menus
      this.headerSection.querySelectorAll('details').forEach((details) => {
        if (details.hasAttribute('open')) {
          details.open = false
        }
      })
    }

    // Hide header when scrolling down, show when scrolling up
    if (scrollTop > this.headerBounds.bottom + this.firstScrollTop + 100) {
      if (scrollTop > this.currentScrollTop) {
        // Scrolling down - hide header
        this.headerSection.classList.add('header-hidden')
      } else {
        // Scrolling up - show header
        this.headerSection.classList.remove('header-hidden')
      }
    } else {
      // Near top of page - always show header
      this.headerSection.classList.remove('header-hidden')
    }

    // Update current scroll position for next comparison
    this.currentScrollTop = scrollTop
  }
}
customElements.define('sticky-header', StickyHeader, { extends: 'header' })

class RevealLink extends HTMLAnchorElement {
  constructor() {
    super()

    this.revealButton = new theme.RevealButton(this)
    this.revealButton.load()
  }
}
customElements.define('reveal-link', RevealLink, { extends: 'a' })

class HoverLink extends HTMLAnchorElement {
  constructor() {
    super()

    this.hoverButton = new theme.HoverButton(this)
    this.hoverButton.load()
  }
}
customElements.define('hover-link', HoverLink, { extends: 'a' })

class MagnetLink extends HoverLink {
  constructor() {
    super()

    this.magnetButton = new theme.MagnetButton(this)
    this.magnetButton.load()
  }
}
customElements.define('magnet-link', MagnetLink, { extends: 'a' })

class HoverButton extends HTMLButtonElement {
  constructor() {
    super()

    this.hoverButton = new theme.HoverButton(this)
    this.hoverButton.load()

    if (this.type === 'submit' && this.form) {
      this.form.addEventListener('submit', () => this.setAttribute('aria-busy', 'true'))
    }
    window.addEventListener('pageshow', () => this.removeAttribute('aria-busy'))

    Motion.inView(this, this.init.bind(this))
  }

  static get observedAttributes() {
    return ['aria-busy']
  }

  get contentElement() {
    return (this._contentElement = this._contentElement || this.querySelector('.btn-text'))
  }

  get animationElement() {
    return (this._animationElement =
      this._animationElement ||
      document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild)
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  init() {
    this.append(this.animationElement)
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === 'true') {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }]
      ])
      Motion.animate(
        this.animationElement.children,
        { transform: ['scale(1.6)', 'scale(0.6)'] },
        { duration: 0.35, delay: Motion.stagger(0.35 / 2), direction: 'alternate', repeat: Infinity }
      )
    } else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }]
      ])
    }
  }
}
customElements.define('hover-button', HoverButton, { extends: 'button' })

class MagnetButton extends HoverButton {
  constructor() {
    super()

    this.magnetButton = new theme.MagnetButton(this)
    this.magnetButton.load()
  }
}
customElements.define('magnet-button', MagnetButton, { extends: 'button' })

class HoverElement extends HTMLElement {
  constructor() {
    super()

    this.hoverButton = new theme.HoverButton(this)
    this.hoverButton.load()
  }
}
customElements.define('hover-element', HoverElement)

class MagnetElement extends HoverElement {
  constructor() {
    super()

    this.magnetButton = new theme.MagnetButton(this)
    this.magnetButton.load()
  }
}
customElements.define('magnet-element', MagnetElement)

class AnimateElement extends HTMLElement {
  constructor() {
    super()

    this.loadPromise = new Promise((resolve) => {
      this.resolveLoad = resolve
    })
  }

  connectedCallback() {
    if (theme.config.motionReduced) {
      this.resolveLoad()
      return
    }

    this.animation = new theme.Animation(this)
    this.animation.beforeLoad()

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media)
      setTimeout(this.resolveLoad, 500)
      await this.animation.load()
      this.parentElement.classList.add('animated')
    })
  }

  reset() {
    this.animation?.reset()
  }

  refresh() {
    this.animation?.reload()
  }
}
customElements.define('animate-element', AnimateElement)

class AnimatePicture extends HTMLPictureElement {
  constructor() {
    super()
  }

  connectedCallback() {
    if (theme.config.motionReduced) return

    this.animation = new theme.Animation(this)
    this.animation.beforeLoad()

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media)
      await this.animation.load()
    })
  }
}
customElements.define('animate-picture', AnimatePicture, { extends: 'picture' })

class AnnouncementBar extends HTMLElement {
  constructor() {
    super()

    this.selectedIndex = this.selectedIndex

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' })
    } else {
      new theme.initWhenVisible(this.init.bind(this))
    }
  }

  static get observedAttributes() {
    return ['selected-index']
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString())
  }

  get items() {
    return (this._items = this._items || Array.from(this.children))
  }

  get autoplay() {
    return this.hasAttribute('autoplay')
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        fade: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        on: {
          ready: function () {
            setTimeout(() => this.element.setAttribute('loaded', ''))
          }
        }
      })

      this.slider.on('change', this.onChange.bind(this))
      this.addEventListener('slider:previous', () => this.slider.previous())
      this.addEventListener('slider:next', () => this.slider.next())
      this.addEventListener('slider:play', () => this.slider.playPlayer())
      this.addEventListener('slider:pause', () => this.slider.pausePlayer())

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)))
      }
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const focusableEvents = 'button, [href]'

      const fromElement = this.items[parseInt(oldValue)]
      const toElement = this.items[parseInt(newValue)]

      fromElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.setAttribute('tabindex', '-1')
      })
      toElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.removeAttribute('tabindex')
      })
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.slider.selectedIndex } }))
  }
}
customElements.define('announcement-bar', AnnouncementBar)

class AccordionsDetails extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('toggle', this.onToggle)
  }

  get items() {
    return (this._items = this._items || Array.from(this.querySelectorAll('details[is="accordion-details"]')))
  }

  onToggle(event) {
    const { current: target, open } = event.detail
    this.items.forEach((item) => {
      if (item !== target) {
        item.close()
      }
    })

    if (open) {
      let headerHeight = 0
      if (!theme.config.mqlSmall && document.querySelector('header.header')) {
        headerHeight = Math.round(document.querySelector('header.header').clientHeight)
      }
      setTimeout(() => {
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - headerHeight,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth'
        })
      }, 250)
    }
  }
}
customElements.define('accordions-details', AccordionsDetails)

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super()

    this._open = this.hasAttribute('open')
    this.summaryElement = this.querySelector('summary')
    this.contentElement = this.querySelector('summary + *')
    this.setAttribute('aria-expanded', this._open ? 'true' : 'false')

    this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this))

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        if (this.designModeActive) this.open = true
      })
      this.addEventListener('shopify:block:deselect', () => {
        if (this.designModeActive) this.open = false
      })
    }
  }

  get designModeActive() {
    return true
  }

  get controlledElement() {
    return this.closest('accordions-details')
  }

  static get observedAttributes() {
    return ['open']
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false')
    }
  }

  get open() {
    return this._open
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value

      if (this.isConnected) {
        this.transition(value)
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open')
      }
    }

    this.setAttribute('aria-expanded', value ? 'true' : 'false')
    this.dispatchEventHandler()
  }

  onSummaryClick(event) {
    event.preventDefault()
    this.open = !this.open
  }

  close() {
    this._open = false
    this.transition(false)
  }

  async transition(value) {
    this.style.overflow = 'hidden'

    if (value) {
      this.setAttribute('open', '')

      await Motion.timeline([
        [this, { height: [`${this.summaryElement.clientHeight}px`, `${this.scrollHeight}px`] }, { duration: 0.25, easing: 'ease' }],
        [this.contentElement, { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 0.15, at: '-0.1' }]
      ]).finished
    } else {
      await Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this, { height: [`${this.clientHeight}px`, `${this.summaryElement.clientHeight}px`] }, { duration: 0.25, at: '<', easing: 'ease' }]
      ]).finished

      this.removeAttribute('open')
    }

    this.style.height = 'auto'
    this.style.overflow = 'visible'
  }

  dispatchEventHandler() {
    ;(this.controlledElement ?? this).dispatchEvent(new CustomEvent('toggle', { bubbles: true, detail: { current: this, open: this.open } }))
  }
}
customElements.define('accordion-details', AccordionDetails, { extends: 'details' })

class FooterDetails extends AccordionDetails {
  constructor() {
    super()

    this.load()
    document.addEventListener('matchSmall', this.load.bind(this))
    document.addEventListener('unmatchSmall', this.load.bind(this))
  }

  get designModeActive() {
    return theme.config.mqlSmall
  }

  get openDefault() {
    return this.hasAttribute('data-opened')
  }

  load() {
    if (!theme.config.mqlSmall || this.openDefault) {
      this.open = true
    } else {
      if (this.open && !this.openDefault) {
        this.open = false
      }
    }
  }
}
customElements.define('footer-details', FooterDetails, { extends: 'details' })

class GestureElement extends HTMLElement {
  constructor() {
    super()
    this.config = {
      thresholdY: Math.max(25, Math.floor(0.15 * window.innerHeight)),
      velocityThreshold: 10,
      disregardVelocityThresholdY: Math.floor(0.5 * this.clientHeight),
      pressThreshold: 8,
      diagonalSwipes: false,
      diagonalLimit: Math.tan(((45 * 1.5) / 180) * Math.PI),
      longPressTime: 500
    }

    this.handlers = {
      panstart: [],
      panmove: [],
      panend: [],
      swipeup: [],
      swipedown: [],
      longpress: []
    }

    Motion.inView(this, this.init.bind(this))
  }

  init() {
    this.addEventListener('touchstart', this.onTouchStart.bind(this), theme.supportsPassive ? { passive: true } : false)
    this.addEventListener('touchmove', this.onTouchMove.bind(this), theme.supportsPassive ? { passive: true } : false)
    this.addEventListener('touchend', this.onTouchEnd.bind(this), theme.supportsPassive ? { passive: true } : false)
  }

  on(type, fn) {
    if (this.handlers[type]) {
      this.handlers[type].push(fn)
      return {
        type,
        fn,
        cancel: () => this.off(type, fn)
      }
    }
  }

  off(type, fn) {
    if (this.handlers[type]) {
      const idx = this.handlers[type].indexOf(fn)
      if (idx !== -1) {
        this.handlers[type].splice(idx, 1)
      }
    }
  }

  fire(type, event) {
    for (let i = 0; i < this.handlers[type].length; i++) {
      this.handlers[type][i](event)
    }
  }

  onTouchStart(event) {
    this.thresholdY = this.config.thresholdY
    this.disregardVelocityThresholdY = this.config.disregardVelocityThresholdY
    this.touchStartY = event.changedTouches[0].screenY
    this.touchMoveY = null
    this.touchEndY = null
    this.swipingDirection = null

    // Long press.
    this.longPressTimer = setTimeout(() => this.fire('longpress', event), this.config.longPressTime)
    this.fire('panstart', event)
  }

  onTouchMove(event) {
    const touchMoveY = event.changedTouches[0].screenY - (this.touchStartY ?? 0)
    this.velocityY = touchMoveY - (this.touchMoveY ?? 0)
    this.touchMoveY = touchMoveY
    const absTouchMoveY = Math.abs(this.touchMoveY)
    this.swipingVertical = absTouchMoveY > this.thresholdY
    this.swipingDirection = this.swipingVertical ? 'vertical' : 'pre-vertical'

    if (absTouchMoveY > this.config.pressThreshold) {
      clearTimeout(this.longPressTimer ?? undefined)
    }
    this.fire('panmove', event)
  }

  onTouchEnd(event) {
    this.touchEndY = event.changedTouches[0].screenY
    this.fire('panend', event)
    clearTimeout(this.longPressTimer ?? undefined)

    const y = this.touchEndY - (this.touchStartY ?? 0)
    const absY = Math.abs(y)

    if (absY > this.thresholdY) {
      this.swipedVertical = this.config.diagonalSwipes ? y <= this.config.diagonalLimit : absY > this.thresholdY
      if (this.swipedVertical) {
        if (y < 0) {
          // Upward swipe.
          if ((this.velocityY ?? 0) < -this.config.velocityThreshold || y < -this.disregardVelocityThresholdY) {
            this.fire('swipeup', event)
          }
        } else {
          // Downward swipe.
          if ((this.velocityY ?? 0) > this.config.velocityThreshold || y > this.disregardVelocityThresholdY) {
            this.fire('swipedown', event)
          }
        }
      }
    }
  }
}
customElements.define('gesture-element', GestureElement)

class OverlayElement extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('mousemove', this.onMouseMove)
    this.addEventListener('mouseleave', this.onMouseLeave)
    this.addEventListener('mousedown', this.onMouseDown)
    this.addEventListener('mouseup', this.onMouseUp)
  }

  get cursor() {
    return document.querySelector('mouse-cursor')
  }

  onMouseMove() {
    this.cursor.classList.add('active')
  }
  onMouseLeave() {
    this.cursor.classList.remove('active')
  }
  onMouseDown() {
    this.cursor.classList.add('pressed')
  }
  onMouseUp() {
    this.cursor.classList.remove('pressed')
  }
}
customElements.define('overlay-element', OverlayElement)

const lockLayerCount = new WeakMap()
class ModalElement extends HTMLElement {
  constructor() {
    super()

    this.events = {
      afterHide: 'modal:afterHide',
      afterShow: 'modal:afterShow',
      closeAll: 'modal:closeAll'
    }

    this.classes = {
      open: 'has-modal-open',
      opening: 'has-modal-opening'
    }
  }

  static get observedAttributes() {
    return ['id', 'open']
  }

  get shouldLock() {
    return false
  }

  get shouldAppendToBody() {
    return false
  }

  get shouldCloseAll() {
    return false
  }

  get open() {
    return this.hasAttribute('open')
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`))
  }

  get overlay() {
    return (this._overlay = this._overlay || this.querySelector('.fixed-modal'))
  }

  get gesture() {
    return (this._gesture = this._gesture || this.querySelector('gesture-element'))
  }

  get designMode() {
    return this.hasAttribute('shopify-design-mode')
  }

  get focusElement() {
    return this.querySelector('gesture-element') || this.querySelector('button')
  }

  connectedCallback() {
    this.abortController = new AbortController()

    this.controls.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this), { signal: this.abortController.signal }))
    document.addEventListener('keyup', (event) => event.code === 'Escape' && this.hide(), { signal: this.abortController.signal })
    document.addEventListener(this.events.closeAll, () => this.hide(), { signal: this.abortController.signal })

    if (this.gesture) {
      this.gestureConfig = {
        animationFrame: null,
        moveY: 0,
        maxGestureDistance: 0,
        endPoint: 0,
        layerHeight: null
      }

      this.gestureWrap = this.gesture.parentElement

      setTimeout(() => {
        this.gesture.on('panstart', this.onPanStart.bind(this))
        this.gesture.on('panmove', this.onPanMove.bind(this))
        this.gesture.on('panend', this.onPanEnd.bind(this))
      }, 75)
    }

    if (Shopify.designMode && this.designMode) {
      const section = this.closest('.shopify-section')
      section.addEventListener('shopify:section:select', (event) => this.show(null, !event.detail.load), { signal: this.abortController.signal })
      section.addEventListener('shopify:section:deselect', this.hide.bind(this), { signal: this.abortController.signal })
    }
  }

  disconnectedCallback() {
    this.abortController?.abort()

    if (Shopify.designMode) {
      document.body.classList.remove(this.classes.open)
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'))

        if (oldValue === null && (newValue === '' || newValue === 'immediate')) {
          this.hidden = false
          this.removeAttribute('inert')

          this.originalParentBeforeAppend = null
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this.originalParentBeforeAppend = this.parentElement
            document.body.append(this)
          }
          const showTransitionPromise = this.showTransition(newValue !== 'immediate') || Promise.resolve()
          showTransitionPromise.then(() => {
            this.afterShow()
            this.dispatchEvent(new CustomEvent(this.events.afterShow, { bubbles: true }))
          })
        } else if (oldValue !== null && newValue === null) {
          this.setAttribute('inert', '')

          const hideTransitionPromise = this.hideTransition() || Promise.resolve()
          hideTransitionPromise.then(() => {
            this.afterHide()

            if (!this.hasAttribute('inert')) return

            if (this.parentElement === document.body && this.originalParentBeforeAppend) {
              this.originalParentBeforeAppend.appendChild(this)
              this.originalParentBeforeAppend = null
            }
            this.dispatchEvent(new CustomEvent(this.events.afterHide, { bubbles: true }))

            this.hidden = true
          })
        }
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }))

        break
    }
  }

  onButtonClick(event) {
    event.preventDefault()

    this.open ? this.hide() : this.show(event.currentTarget)
  }

  hide() {
    if (!this.open) return

    this.beforeHide()
    this.resetGesture()
    this.removeAttribute('open')

    return theme.utils.waitForEvent(this, this.events.afterHide)
  }
  show(activeElement = null, animate = true) {
    if (this.open) return

    if (this.shouldCloseAll) {
      document.dispatchEvent(new CustomEvent(this.events.closeAll), { bubbles: true })
    }

    this.beforeShow()
    this.activeElement = activeElement
    this.setAttribute('open', animate ? '' : 'immediate')

    if (this.shouldLock) {
      document.body.classList.add(this.classes.opening)
    }

    return theme.utils.waitForEvent(this, this.events.afterShow)
  }

  beforeHide() {}
  beforeShow() {}

  afterHide() {
    setTimeout(() => {
      theme.a11y.removeTrapFocus(this.activeElement)
      if (this.shouldLock) {
        lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) - 1)

        document.body.classList.toggle(this.classes.open, lockLayerCount.get(ModalElement) > 0)
      }
    })
  }
  afterShow() {
    theme.a11y.trapFocus(this, this.focusElement)
    if (this.shouldLock) {
      lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) + 1)

      document.body.classList.remove(this.classes.opening)
      document.body.classList.add(this.classes.open)
    }
  }

  showTransition() {
    setTimeout(() => {
      this.setAttribute('active', '')
    }, 75)
    return new Promise((resolve) => {
      const computedStyle = window.getComputedStyle(this.overlay)
      const hasTransition = computedStyle.transitionProperty !== 'none' && parseFloat(computedStyle.transitionDuration) > 0

      if (!hasTransition) {
        resolve()
        return
      }

      this.overlay.addEventListener('transitionend', resolve, { once: true })
    })
  }
  hideTransition() {
    this.removeAttribute('active')
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, { once: true })
    })
  }

  resetGesture() {
    if (this.gesture) {
      this.gestureWrap.style.transform = ''
      this.gestureWrap.style.transition = ''
      this.overlay.style.opacity = ''
      this.overlay.style.transition = ''
    }
  }

  onPanStart() {
    this.removeTransition(this.gestureWrap, 'transform')

    if (this.hasAttribute('open')) {
      if (this.gestureConfig.layerHeight === null) {
        this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height
      }
      this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50
      this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3
    }
  }

  onPanMove() {
    if (this.gestureConfig.animationFrame) return

    this.gestureConfig.animationFrame = requestAnimationFrame(() => {
      const clamp = (a, min = 0, max = 1) => Math.min(max, Math.max(min, a))
      const invlerp = (x, y, a) => clamp((a - x) / (y - x))

      // Give an indication of whether we've passed the swiping threshold.
      this.gestureWrap.style.transition = 'transform .1s linear'
      this.overlay.style.transition = 'opacity .1s linear'

      if (this.hasAttribute('open')) {
        if (this.gestureConfig.layerHeight === null) {
          this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height
        }

        this.gestureConfig.moveY = this.gesture.touchMoveY
        this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50

        if (this.gestureConfig.moveY >= 0) {
          this.gestureWrap.style.transform = `translateY(${Math.min(this.gestureConfig.moveY, this.gestureConfig.maxGestureDistance)}px)`

          this.overlay.style.opacity = invlerp(this.gestureConfig.layerHeight, 0, this.gestureConfig.moveY)
        } else {
          this.gestureWrap.style.transform = 'translateY(0)'
          this.gestureWrap.style.transition = ''
          this.overlay.style.opacity = '1'
        }
      }

      this.gestureConfig.animationFrame = null
    })
  }

  onPanEnd() {
    this.gestureConfig.animationFrame === null || cancelAnimationFrame(this.gestureConfig.animationFrame)
    this.gestureConfig.animationFrame = null
    this.gestureWrap.style.transition = 'transform .1s linear'

    if (this.gestureConfig.layerHeight === null) {
      this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height
    }

    this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3

    if (this.hasAttribute('open')) {
      this.gestureConfig.moveY = this.gesture.touchMoveY

      if (this.gestureConfig.moveY < this.gestureConfig.endPoint) {
        this.gestureWrap.style.transform = 'translateY(0)'
      } else {
        this.gestureWrap.style.transform = `translateY(${this.gestureConfig.layerHeight}px)`

        this.gestureWrap.addEventListener(
          this.whichTransitionEvent(),
          () => {
            this.hide()
          },
          { once: true }
        )
      }
    }
  }

  removeTransition(node, transition) {
    const match = node.style.transition.match(new RegExp('(?:^|,)\\s*' + transition + '(?:$|\\s|,)[^,]*', 'i'))
    if (match) {
      const transitionArray = node.style.transition.split('')
      transitionArray.splice(match.index, match[0].length)
      node.style.transition = transitionArray.join('')
    }
  }

  whichTransitionEvent() {
    let t
    const el = document.createElement('fakeelement')
    const transitions = {
      WebkitTransition: 'webkitTransitionEnd',
      MozTransition: 'transitionend',
      MSTransition: 'msTransitionEnd',
      OTransition: 'oTransitionEnd',
      transition: 'transitionEnd'
    }

    for (t in transitions) {
      if (el.style[t] !== undefined) {
        return transitions[t]
      }
    }
  }
}
customElements.define('modal-element', ModalElement)
lockLayerCount.set(ModalElement, 0)

class DrawerElement extends ModalElement {
  constructor() {
    super()

    this.events = {
      afterHide: 'drawer:afterHide',
      afterShow: 'drawer:afterShow'
    }
  }

  get shouldLock() {
    return true
  }

  get shouldAppendToBody() {
    return true
  }
}
customElements.define('drawer-element', DrawerElement)

class MenuDrawer extends DrawerElement {
  constructor() {
    super()
  }

  get menuItems() {
    return (this._menuItems = this._menuItems || this.querySelectorAll('.drawer__menu:not(.active)>li'))
  }

  beforeShow() {
    super.beforeShow()
    setTimeout(() => {
      Motion.animate(
        this.menuItems,
        { transform: ['translateX(-20px)', 'translateX(0)'], opacity: [0, 1] },
        { duration: 0.6, easing: [0.075, 0.82, 0.165, 1], delay: Motion.stagger(0.1) }
      ).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute('style'))
      })
    }, 300)
  }

  beforeHide() {
    super.beforeHide()

    setTimeout(() => {
      this.querySelectorAll('details[is=menu-details]').forEach((subMenu) => {
        subMenu.onCloseButtonClick()
      })
    }, 300)
  }
}
customElements.define('menu-drawer', MenuDrawer)

class ShareDrawer extends DrawerElement {
  constructor() {
    super()
  }

  get menuItems() {
    return (this._menuItems = this._menuItems || this.querySelectorAll('.share-buttons>li'))
  }

  get urlToShare() {
    const urlInput = this.querySelector('input[type=hidden]')
    return urlInput ? urlInput.value : document.location.href
  }

  connectedCallback() {
    if (navigator.share) {
      this.controls.forEach((button) => button.addEventListener('click', this.shareTo.bind(this)))
    } else {
      super.connectedCallback()
    }
  }

  async shareTo(event) {
    event.preventDefault()
    try {
      await navigator.share({ url: this.urlToShare, title: document.title })
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Share canceled by user')
      } else {
        console.error(error)
      }
    }
  }

  beforeShow() {
    super.beforeShow()
    setTimeout(() => {
      Motion.animate(
        this.menuItems,
        { transform: ['translateY(2.5rem)', 'translateY(0)'], opacity: [0, 1] },
        { duration: 0.6, easing: [0.075, 0.82, 0.165, 1], delay: Motion.stagger(0.1) }
      ).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute('style'))
      })
    }, 300)
  }
}
customElements.define('share-drawer', ShareDrawer)

class MenuDetails extends HTMLDetailsElement {
  constructor() {
    super()

    this.summary.addEventListener('click', this.onSummaryClick.bind(this))
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this))
  }

  get parent() {
    return this.closest('[data-parent]')
  }

  get summary() {
    return this.querySelector('summary')
  }

  get closeButton() {
    return this.querySelector('[data-close]')
  }

  get focusElement() {
    return this.summary.nextElementSibling.querySelector('button')
  }

  onSummaryClick(event) {
    event.preventDefault()
    this.setAttribute('open', '')
    this.activeElement = event.currentTarget

    setTimeout(() => {
      this.parent.classList.add('active')
      this.classList.add('active')
      this.summary.setAttribute('aria-expanded', true)
      this.summary.setAttribute('tabindex', '-1')

      setTimeout(() => {
        theme.a11y.trapFocus(this, this.focusElement)
      }, 100)
    }, 100)
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active')
    this.classList.remove('active')
    this.summary.setAttribute('aria-expanded', false)
    this.summary.removeAttribute('tabindex')

    this.closeAnimation()
  }

  closeAnimation() {
    let animationStart

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time
      }

      const elapsedTime = time - animationStart

      if (elapsedTime < 400) {
        requestAnimationFrame(handleAnimation)
      } else {
        this.removeAttribute('open')
        theme.a11y.removeTrapFocus(this.activeElement)
      }
    }

    requestAnimationFrame(handleAnimation)
  }
}
customElements.define('menu-details', MenuDetails, { extends: 'details' })

class QuantityLabel extends HTMLLabelElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.querySelector('.quantity__rules-cart').append(this.animationElement)
  }

  static get observedAttributes() {
    return ['aria-busy']
  }

  get contentElement() {
    return (this._contentElement = this._contentElement || this.querySelector('.quantity-cart'))
  }

  get animationElement() {
    return (this._animationElement =
      this._animationElement ||
      document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild)
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === 'true') {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }]
      ])
      Motion.animate(
        this.animationElement.children,
        { transform: ['scale(1.6)', 'scale(0.6)'] },
        { duration: 0.35, delay: Motion.stagger(0.35 / 2), direction: 'alternate', repeat: Infinity }
      )
    } else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }]
      ])
    }
  }
}
customElements.define('quantity-label', QuantityLabel, { extends: 'label' })

class QuantityInput extends HTMLElement {
  quantityUpdateUnsubscriber = undefined
  quantityBoundriesUnsubscriber = undefined
  quantityRulesUnsubscriber = undefined

  constructor() {
    super()
  }

  get sectionId() {
    return this.getAttribute('data-section-id')
  }

  get productId() {
    return this.getAttribute('data-product-id')
  }

  get input() {
    return this.querySelector('input')
  }

  get value() {
    return this.input.value
  }

  connectedCallback() {
    this.abortController = new AbortController()

    this.buttons = Array.from(this.querySelectorAll('button'))
    this.changeEvent = new Event('change', { bubbles: true })
    this.input.addEventListener('change', this.onInputChange.bind(this))
    this.input.addEventListener('focus', () => setTimeout(() => this.input.select()))

    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)), { signal: this.abortController.signal })

    this.validateQtyRules()
    this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this))
    this.quantityBoundriesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, this.setQuantityBoundries.bind(this))
    this.quantityRulesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityRules, this.updateQuantityRules.bind(this))
  }

  disconnectedCallback() {
    this.abortController.abort()

    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber()
    }
    if (this.quantityBoundriesUnsubscriber) {
      this.quantityBoundriesUnsubscriber()
    }
    if (this.quantityRulesUnsubscriber) {
      this.quantityRulesUnsubscriber()
    }
  }

  onButtonClick(event) {
    event.preventDefault()
    if (this.input.disabled) return
    const previousValue = this.input.value

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.getAttribute('data-min')) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.getAttribute('data-min')
      } else {
        this.input.stepUp()
      }
    } else {
      this.input.stepDown()
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent)

    if (this.input.getAttribute('data-min') === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min)
    }
  }

  onInputChange() {
    if (this.input.value === '') {
      this.input.value = parseInt(this.input.min)
    }
    this.validateQtyRules()
  }

  validateQtyRules() {
    const value = parseInt(this.input.value)
    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]')
      if (buttonMinus) buttonMinus.toggleAttribute('disabled', parseInt(value) <= parseInt(this.input.min))
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector('button[name="plus"]')
      if (buttonPlus) buttonPlus.toggleAttribute('disabled', parseInt(value) >= parseInt(this.input.max))
    }
  }

  updateQuantityRules({ data: { sectionId, productId, parsedHTML } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return

    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label']
    const quantityFormUpdated = parsedHTML.getElementById(`QuantityForm-${sectionId}-${this.productId}`)
    const quantityForm = this.closest(`#QuantityForm-${sectionId}-${this.productId}`)
    for (let selector of selectors) {
      const current = quantityForm.querySelector(selector)
      const updated = quantityFormUpdated.querySelector(selector)
      if (!current || !updated) continue

      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step']
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute)
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated)
          } else {
            current.removeAttribute(attribute)
          }
        }
      } else {
        current.innerHTML = updated.innerHTML
      }
    }
  }

  setQuantityBoundries({ data: { sectionId, productId } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return

    const data = {
      cartQuantity: this.input.hasAttribute('data-cart-quantity') ? parseInt(this.input.getAttribute('data-cart-quantity')) : 0,
      min: this.input.hasAttribute('data-min') ? parseInt(this.input.getAttribute('data-min')) : 1,
      max: this.input.hasAttribute('data-max') ? parseInt(this.input.getAttribute('data-max')) : null,
      step: this.input.hasAttribute('step') ? parseInt(this.input.getAttribute('step')) : 1
    }

    let min = data.min
    const max = data.max === null ? data.max : data.max - data.cartQuantity
    if (max !== null) min = Math.min(min, max)
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

    this.input.min = min

    if (max) {
      this.input.max = max
    } else {
      this.input.removeAttribute('max')
    }
    this.input.value = min

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined)
  }

  reset() {
    this.input.value = this.input.defaultValue
  }
}
customElements.define('quantity-input', QuantityInput)

class CartCount extends HTMLElement {
  constructor() {
    super()
  }

  cartUpdateUnsubscriber = undefined

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this))
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber()
    }
  }

  get itemCount() {
    return parseInt(this.innerText)
  }

  onCartUpdate(event) {
    if (event.cart.errors) return

    this.innerText = event.cart.item_count
    this.hidden = this.itemCount === 0
  }
}
customElements.define('cart-count', CartCount)

class SplitWords extends HTMLElement {
  constructor() {
    super()

    this.loadPromise = new Promise((resolve) => {
      this.resolveLoad = resolve
    })
  }

  connectedCallback() {
    if (theme.config.motionReduced) {
      this.resolveLoad()
      return
    }
    if (!document.body.hasAttribute('data-title-animation')) return

    this.init()
  }

  async init() {
    const splitting = Splitting({ target: this, by: 'words' })

    splitting[0].words.forEach((item, index) => {
      const wrapper = document.createElement('animate-element')
      wrapper.className = 'block'
      wrapper.setAttribute('data-animate', this.getAttribute('data-animate'))
      wrapper.setAttribute(
        'data-animate-delay',
        (this.hasAttribute('data-animate-delay') ? parseInt(this.getAttribute('data-animate-delay')) : 0) + index * 30
      )

      for (const itemContent of item.childNodes) {
        wrapper.appendChild(itemContent)
      }

      item.appendChild(wrapper)
    })
    this.classList.remove('js-invisible')

    const animateElements = this.querySelectorAll('animate-element')
    await Promise.all([...animateElements].map((el) => el.loadPromise))
    this.resolveLoad()
  }
}
customElements.define('split-words', SplitWords)

class HighlightedText extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this))
  }

  get splitWords() {
    return this.closest('split-words')
  }

  async init() {
    if (this.splitWords) {
      await this.splitWords.loadPromise
    }
    this.classList.add('animated')
  }
}
customElements.define('highlighted-text', HighlightedText, { extends: 'em' })

class MarqueeElement extends HTMLElement {
  constructor() {
    super()

    if (theme.config.motionReduced) return

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' })
    } else {
      new theme.initWhenVisible(this.init.bind(this))
    }
  }

  get childElement() {
    return this.firstElementChild
  }

  get speed() {
    return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed')) : 16
  }

  get maximum() {
    return this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : Math.ceil(this.parentWidth / this.childElementWidth) + 2
  }

  get direction() {
    return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left'
  }

  get parallax() {
    return !theme.config.isTouch && this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false
  }

  get parentWidth() {
    return this.getWidth(this)
  }

  get childElementWidth() {
    return this.getWidth(this.childElement)
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    if (this.childElementCount === 1) {
      this.childElement.classList.add('animated')

      const maximum = this.maximum
      for (let index = 0; index < maximum; index++) {
        this.clone = this.childElement.cloneNode(true)
        this.clone.setAttribute('aria-hidden', true)
        this.appendChild(this.clone)
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'))
      }

      this.style.setProperty('--duration', `${(33 - this.speed) * Math.min(2.5, Math.ceil(this.childElementWidth / this.parentWidth))}s`)
    }

    if (this.parallax) {
      let translate = (this.parallax * 100) / (1 + this.parallax)
      if (this.direction === 'right') {
        translate = translate * -1
      }
      if (theme.config.rtl) {
        translate = translate * -1
      }

      Motion.scroll(Motion.animate(this, { transform: [`translateX(${translate}%)`, `translateX(0)`] }, { easing: 'linear' }), {
        target: this,
        offset: ['start end', 'end start']
      })
    } else {
      // pause when out of view
      const observer = new IntersectionObserver(
        (entries, _observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.classList.remove('paused')
            } else {
              this.classList.add('paused')
            }
          })
        },
        { rootMargin: '0px 0px 50px 0px' }
      )
      observer.observe(this)
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect()
    return rect.right - rect.left
  }
}
customElements.define('marquee-element', MarqueeElement)

class ScrolledImages extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' })
  }

  get parallax() {
    return this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false
  }

  get template() {
    return this.querySelector('template')
  }

  get images() {
    return Array.from(this.template.content.querySelectorAll('.scrolled-images__item'))
  }

  init() {
    this.beforeInit()

    if (theme.config.motionReduced || !this.parallax) return
    this.setupParallax()
  }

  beforeInit() {
    const main = this.querySelector('.scrolled-images__main')

    for (let i = 0; i < 3; i++) {
      let images = this.shuffle(this.images)

      if (images.length < 8) {
        let start = 0
        while (images.length < 8) {
          images.push(images[start].cloneNode(true))
          start++
        }
      }

      const row = document.createElement('div')
      row.classList = 'scrolled-images__row'
      images.forEach((item) => row.appendChild(item.cloneNode(true)))

      main.appendChild(row)
    }
  }

  setupParallax() {
    Array.from(this.querySelectorAll('.scrolled-images__row')).forEach((element, index) => {
      let translate = (-1 * this.parallax * 100) / (1 + this.parallax)
      if (theme.config.rtl) {
        translate = translate * -1
      }

      if (index % 2 === 0) {
        Motion.scroll(Motion.animate(element, { transform: [`translateX(${translate}%)`, 'translateX(0)'] }, { easing: 'linear' }), {
          target: this,
          offset: Motion.ScrollOffset.Any
        })
      } else {
        Motion.scroll(Motion.animate(element, { transform: ['translateX(0)', `translateX(${translate}%)`] }, { easing: 'linear' }), {
          target: this,
          offset: Motion.ScrollOffset.Any
        })
      }
    })
  }

  shuffle(arr) {
    return arr.sort(() => Math.random() - 0.5)
  }
}
customElements.define('scrolled-images', ScrolledImages)
class DropdownElement extends HTMLElement {
  constructor() {
    super()

    this.classes = {
      bodyClass: 'has-dropdown'
    }

    this.events = {
      afterHide: 'dropdown:afterHide',
      afterShow: 'dropdown:afterShow'
    }

    this.detectHoverListener = this.detectHover.bind(this)
    this.controls.forEach((button) => {
      button.addEventListener('click', this.onToggleClicked.bind(this))
      button.addEventListener('mouseenter', this.detectHoverListener.bind(this))
      button.addEventListener('mouseleave', this.detectHoverListener.bind(this))
    })

    this.detectClickOutsideListener = this.detectClickOutside.bind(this)
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this)
    this.detectFocusOutListener = this.detectFocusOut.bind(this)
  }

  static get observedAttributes() {
    return ['id', 'open']
  }

  get open() {
    return this.hasAttribute('open')
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`))
  }

  get container() {
    return this.querySelector('*:first-child')
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'))
        break
    }
  }

  show() {
    document.body.classList.add(this.classes.bodyClass)
    this.setAttribute('open', '')

    document.addEventListener('click', this.detectClickOutsideListener)
    document.addEventListener('keyup', this.detectEscKeyboardListener)
    document.addEventListener('focusout', this.detectFocusOutListener)

    this.afterShow()

    return theme.utils.waitForEvent(this, this.events.afterShow)
  }

  hide() {
    document.body.classList.remove(this.classes.bodyClass)
    this.removeAttribute('open')

    document.removeEventListener('click', this.detectClickOutsideListener)
    document.removeEventListener('keyup', this.detectEscKeyboardListener)
    document.removeEventListener('focusout', this.detectFocusOutListener)

    this.afterHide()

    return theme.utils.waitForEvent(this, this.events.afterHide)
  }

  onToggleClicked(event) {
    event?.preventDefault()
    this.open ? this.hide() : this.show()
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] })
    Motion.animate(
      this.container,
      { transform: ['translateY(-105%)', 'translateY(0)'] },
      { duration: theme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] }
    )
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [0.7, 0, 0.2, 1] })
    Motion.animate(this.container, { transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] })
  }

  detectClickOutside(event) {
    if (!this.parentElement.contains(event.target)) {
      this.hide()
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      this.hide()
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.hide()
    }
  }

  detectHover(event) {
    if (theme.config.isTouch) return

    if (event.type === 'mouseenter') {
      this.show()
    } else {
      this.hide()
    }
  }
}
customElements.define('dropdown-element', DropdownElement)

class DropdownLocalization extends DropdownElement {
  constructor() {
    super()
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] })
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [0.7, 0, 0.2, 1] })
  }
}
customElements.define('dropdown-localization', DropdownLocalization)

const lockDropdownCount = new WeakMap()
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super()

    this.classes = {
      bodyClass: 'has-dropdown-menu'
    }

    this.events = {
      afterHide: 'menu:afterHide',
      afterShow: 'menu:afterShow'
    }

    this._open = this.hasAttribute('open')
    this.onSummaryClickListener = this.onSummaryClicked.bind(this)

    if (this.summaryElement) {
      this.summaryElement.addEventListener('click', this.onSummaryClickListener)
    } else {
      requestAnimationFrame(() => {
        this.summaryElement?.addEventListener('click', this.onSummaryClickListener)
      })
    }

    this.detectClickOutsideListener = this.detectClickOutside.bind(this)
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this)
    this.detectFocusOutListener = this.detectFocusOut.bind(this)

    this.hoverTimer = null
    this.detectHoverListener = this.detectHover.bind(this)
    this.addEventListener('mouseenter', this.detectHoverListener.bind(this))
    this.addEventListener('mouseleave', this.detectHoverListener.bind(this))
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value

      if (this.isConnected) {
        this.transition(value)
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open')
      }
    }
  }

  get summaryElement() {
    return this.firstElementChild
  }

  get contentElement() {
    return this.lastElementChild
  }

  get open() {
    return this._open
  }

  get trigger() {
    return this.hasAttribute('trigger') ? this.getAttribute('trigger') : 'click'
  }

  get level() {
    return this.hasAttribute('level') ? this.getAttribute('level') : 'top'
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  onSummaryClicked(event) {
    event.preventDefault()

    if (
      !theme.config.isTouch &&
      this.trigger === 'hover' &&
      this.summaryElement.hasAttribute('data-link') &&
      this.summaryElement.getAttribute('data-link').length > 0
    ) {
      window.location.href = this.summaryElement.getAttribute('data-link')
    } else {
      this.open = !this.open
    }
  }

  async transition(value) {
    if (value) {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) + 1)
      document.body.classList.add(this.classes.bodyClass)

      this.setAttribute('open', '')
      this.summaryElement.setAttribute('open', '')
      if (theme.config.motionReduced) {
        this.contentElement.setAttribute('open', '')
      } else {
        setTimeout(() => this.contentElement.setAttribute('open', ''), 100)
      }
      document.addEventListener('click', this.detectClickOutsideListener)
      document.addEventListener('keyup', this.detectEscKeyboardListener)
      document.addEventListener('focusout', this.detectFocusOutListener)
      await this.transitionIn()
      this.shouldReverse()

      return theme.utils.waitForEvent(this, this.events.afterShow)
    } else {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) - 1)
      document.body.classList.toggle(this.classes.bodyClass, lockDropdownCount.get(DetailsDropdown) > 0)

      this.summaryElement.removeAttribute('open')
      this.contentElement.removeAttribute('open')
      document.removeEventListener('click', this.detectClickOutsideListener)
      document.removeEventListener('keyup', this.detectEscKeyboardListener)
      document.removeEventListener('focusout', this.detectFocusOutListener)
      await this.transitionOut()
      if (!this.open) this.removeAttribute('open')

      return theme.utils.waitForEvent(this, this.events.afterHide)
    }
  }

  async transitionIn() {
    Motion.animate(
      this.contentElement,
      { opacity: [0, 1], visibility: 'visible' },
      { duration: theme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1], delay: theme.config.motionReduced ? 0 : 0.2 }
    )
    const translateY = this.level === 'top' ? '-105%' : '2rem'
    return Motion.animate(
      this.contentElement.firstElementChild,
      { transform: [`translateY(${translateY})`, 'translateY(0)'] },
      { duration: theme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] }
    ).finished
  }

  async transitionOut() {
    Motion.animate(
      this.contentElement,
      { opacity: 0, visibility: 'hidden' },
      { duration: theme.config.motionReduced ? 0 : 0.3, easing: [0.7, 0, 0.2, 1] }
    )
    const translateY = this.level === 'top' ? '-105%' : '2rem'
    return Motion.animate(
      this.contentElement.firstElementChild,
      { transform: `translateY(${translateY})` },
      { duration: theme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] }
    ).finished
  }

  detectClickOutside(event) {
    if (!this.contains(event.target) && !(event.target.closest('details') instanceof DetailsDropdown)) {
      this.open = false
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]')
      if (targetMenu) {
        targetMenu.open = false
      }
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false
    }
  }

  detectHover(event) {
    if (this.trigger !== 'hover' || theme.config.isTouch) return

    if (event.type === 'mouseenter') {
      this.open = true
    } else {
      this.open = false
    }
  }

  shouldReverse() {
    const maxWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2
    if (maxWidth > window.innerWidth) {
      this.contentElement.classList.add('should-reverse')
    }
  }
}
customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' })
lockDropdownCount.set(DetailsDropdown, 0)

class DetailsMega extends DetailsDropdown {
  constructor() {
    super()

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => (this.open = true))
      this.addEventListener('shopify:block:deselect', () => (this.open = false))
    }
  }

  async transitionIn() {
    this.contentElement.querySelector('tabs-element')?.unload()
    setTimeout(
      () => {
        this.contentElement.querySelector('tabs-element')?.load()
      },
      theme.config.motionReduced ? 0 : 450
    )

    document.body.classList.add('with-mega')
    return Motion.animate(
      this.contentElement.firstElementChild,
      { visibility: 'visible', transform: ['translateY(-105%)', 'translateY(0)'] },
      { duration: theme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] }
    ).finished
  }

  async transitionOut() {
    document.body.classList.remove('with-mega')
    return Motion.animate(
      this.contentElement.firstElementChild,
      { visibility: 'hidden', transform: 'translateY(-105%)' },
      { duration: theme.config.motionReduced ? 0 : 0.6, easing: [0.7, 0, 0.2, 1] }
    ).finished
  }
}
customElements.define('details-mega', DetailsMega, { extends: 'details' })

class LocalizationListbox extends HTMLFormElement {
  constructor() {
    super()

    this.items.forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)))
  }

  get items() {
    return (this._items = this._items || Array.from(this.querySelectorAll('a')))
  }

  get input() {
    return this.querySelector('input[name="locale_code"], input[name="country_code"]')
  }

  get returnTo() {
    return this.querySelector('input[name="return_to"]')
  }

  onItemClick(event) {
    event.preventDefault()

    this.input.value = event.currentTarget.getAttribute('data-value')
    this.returnTo.value = window.location.pathname
    this.submit()
  }
}
customElements.define('localization-listbox', LocalizationListbox, { extends: 'form' })

class LocalizationForm extends HTMLFormElement {
  constructor() {
    super()

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this))
    } else {
      new theme.initWhenVisible(this.init.bind(this))
    }
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    this.addEventListener('change', this.submit)
  }
}
customElements.define('localization-form', LocalizationForm, { extends: 'form' })

const cachedSectionsRenderingAPI = new Map()
class APIButton extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this))
  }

  get sectionId() {
    return this.getAttribute('data-section-id')
  }

  get sectionRenderingId() {
    return this.getAttribute('data-id')
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    if (document.getElementById(this.sectionRenderingId) === null || document.getElementById(this.sectionRenderingId).hasAttribute('loaded')) return

    const url = `${theme.routes.root_url}?section_id=${this.sectionId}`
    cachedSectionsRenderingAPI.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url)

    this.sectionsRenderingAPIListener = this.afterSectionsRenderingAPI.bind(this)
    document.addEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener)
  }

  renderSectionFromCache(url) {
    const responseText = cachedSectionsRenderingAPI.get(url)
    if (responseText.length) {
      this.updateSectionRendering(responseText)
    }
  }

  renderSectionFromFetch(url) {
    cachedSectionsRenderingAPI.set(url, '')
    theme.utils
      .fetchWithRetry(url, {}, { label: 'section-render' })
      .then((response) => response.text())
      .then((responseText) => {
        this.updateSectionRendering(responseText)
        cachedSectionsRenderingAPI.set(url, responseText)

        document.dispatchEvent(
          new CustomEvent('sectionsRenderingAPI:cached', {
            detail: {
              url,
              sectionId: this.sectionId
            }
          })
        )
      })
      .catch((e) => {
        theme.utils.reportFetchError('section-render', e)
      })
  }

  updateSectionRendering(responseText) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html')
    document.getElementById(this.sectionRenderingId).replaceWith(parsedHTML.getElementById(this.sectionRenderingId))

    // remove listener
    document.removeEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener)
  }

  afterSectionsRenderingAPI(event) {
    if (event.detail.sectionId === this.sectionId) {
      this.renderSectionFromCache(event.detail.url)
    }
  }
}
customElements.define('api-button', APIButton)
class StickyElement extends HTMLElement {
  constructor() {
    super()

    this.endScroll = window.innerHeight - this.offsetHeight - 500
    this.currPos = window.scrollY
    this.screenHeight = window.innerHeight
    this.stickyElementHeight = this.offsetHeight
    this.bottomGap = this.offsetHeight < window.innerHeight ? this.topGap : 0

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), {
      capture: true,
      passive: true
    })
  }

  get headerHeight() {
    let header = document.querySelector('.header-section header[is="sticky-header"]')
    if (this.hasAttribute('aria-controls')) {
      header = document.getElementById(this.getAttribute('aria-controls'))
    }
    return header ? header.getBoundingClientRect().height + this.topGap : this.offsetHeight < window.innerHeight ? this.topGap : 0
  }

  get topGap() {
    return this.hasAttribute('data-gap') ? parseInt(this.getAttribute('data-gap')) : 20
  }

  get inset() {
    return this.style.getPropertyValue('--inset') || 0
  }

  onScrollHandler() {
    const style = window.getComputedStyle(this)
    if (style.position == 'sticky') {
      this.screenHeight = window.innerHeight
      this.stickyElementHeight = this.offsetHeight

      this.positionStickySidebar()
    }
  }

  positionStickySidebar() {
    this.endScroll = window.innerHeight - this.offsetHeight - this.bottomGap
    const stickyElementTop = parseFloat(this.inset)

    if (this.stickyElementHeight + this.headerHeight + this.bottomGap > this.screenHeight) {
      if (window.scrollY < this.currPos) {
        // Scroll up
        if (stickyElementTop < this.headerHeight) {
          this.style.setProperty('--inset', `${(stickyElementTop + this.currPos - window.scrollY).toFixed(1)}px`)
        } else if (stickyElementTop >= this.headerHeight && stickyElementTop !== this.headerHeight) {
          this.style.setProperty('--inset', `${this.headerHeight.toFixed(1)}px`)
        }
      } else {
        // Scroll down
        if (stickyElementTop > this.endScroll) {
          this.style.setProperty('--inset', `${stickyElementTop + this.currPos - window.scrollY.toFixed(1)}px`)
        } else if (stickyElementTop < this.endScroll && stickyElementTop !== this.endScroll) {
          this.style.setProperty('--inset', `${this.endScroll.toFixed(1)}px`)
        }
      }
    } else {
      this.style.setProperty('--inset', `${this.headerHeight.toFixed(1)}px`)
    }

    this.currPos = window.scrollY
  }
}
customElements.define('sticky-element', StickyElement)

class ParallaxElement extends HTMLDivElement {
  constructor() {
    super()

    this.load()

    document.addEventListener('matchSmall', this.unload.bind(this))
    document.addEventListener('unmatchSmall', this.load.bind(this))
  }

  get mobileDisabled() {
    return false
  }

  load() {
    if (theme.config.motionReduced) return
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return

    this.motion()
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: [`translateY(${this.getAttribute('data-start')})`, `translateY(${this.getAttribute('data-stop')})`] }),
      { target: this, offset: Motion.ScrollOffset.Any }
    )
  }

  unload() {
    if (this.animation) {
      this.animation()
      this.style.transform = null
    }
  }
}
customElements.define('parallax-element', ParallaxElement, { extends: 'div' })

class ParallaxOverlay extends HTMLElement {
  constructor() {
    super()

    this.load()

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.refresh.bind(this))
    }
  }

  refresh() {
    let options = {}
    options[this.getAttribute('data-target')] = this.getAttribute('data-stop')

    Motion.animate(this, options, { duration: 0 })
  }

  load() {
    let options = {}
    options[this.getAttribute('data-target')] = [this.getAttribute('data-start'), this.getAttribute('data-stop')]

    this.animation = Motion.scroll(Motion.animate(this, options), { target: this.parentElement, offset: Motion.ScrollOffset.Enter })
  }
}
customElements.define('parallax-overlay', ParallaxOverlay)

class FooterParallax extends ParallaxElement {
  constructor() {
    super()

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.refresh.bind(this))
    }
  }

  get mobileDisabled() {
    return true
  }

  motion() {
    this.animation = Motion.scroll(Motion.animate(this, { transform: ['translateY(-50%)', 'translateY(0)'] }), {
      target: this,
      offset: Motion.ScrollOffset.Enter
    })
  }

  refresh() {
    if (theme.config.motionReduced) return
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return

    setTimeout(() => {
      Motion.animate(this, { transform: 'translateY(0)' }, { duration: 0 })
    })
  }
}
customElements.define('footer-parallax', FooterParallax, { extends: 'div' })

class FooterGroup extends HTMLElement {
  constructor() {
    super()

    this.init()

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.init.bind(this))
    }
  }

  get rounded() {
    return Array.from(this.querySelectorAll('.section--rounded'))
  }

  get sections() {
    return Array.from(this.querySelectorAll('.shopify-section'))
  }

  init() {
    this.sections.forEach((shopifySection, index) => {
      const section = shopifySection.querySelector('.section')
      if (section) {
        section.classList.remove('section--next-rounded')
        section.style.zIndex = this.sections.length - index
      }
    })

    this.rounded.forEach((section) => {
      const shopifySection = section.closest('.shopify-section')
      let previousShopifySection = shopifySection.previousElementSibling

      if (previousShopifySection === null) {
        previousShopifySection = document.querySelector('.main-content .shopify-section:last-child')
      }

      if (previousShopifySection) {
        const previousSection = previousShopifySection.querySelector('.section')
        if (previousSection) {
          previousSection.classList.add('section--next-rounded')
        }
      }
    })
  }
}
customElements.define('footer-group', FooterGroup, { extends: 'footer' })

class CarouselElement extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' })
  }

  get items() {
    return (this._items = this._items || Array.from(this.children))
  }

  get watchCSS() {
    return this.hasAttribute('watch-css')
  }

  get initialIndex() {
    return parseInt(this.getAttribute('initial-index') || 0)
  }

  init() {
    if (this.items.length > 1) {
      this.carousel = new Flickity(this, {
        watchCSS: this.watchCSS,
        prevNextButtons: false,
        adaptiveHeight: true,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        initialIndex: this.initialIndex
      })

      this.addEventListener('control:select', (event) => this.select(event.detail.index))
      this.carousel.on('change', this.onChange.bind(this))

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.carousel.select(this.items.indexOf(event.target)))
      }
    }
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy()
  }

  select(index = 0, immediate = false) {
    if (!immediate) {
      const { selectedIndex, slides } = this.carousel

      immediate = Math.abs(index - selectedIndex) > 3

      if (index === 0 && selectedIndex === slides.length - 1) {
        immediate = false
      }

      if (index === slides.length - 1 && selectedIndex === 0) {
        immediate = false
      }
    }
    this.carousel.select(index, false, immediate)
  }

  onChange(index) {
    this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index } }))
  }
}
customElements.define('carousel-element', CarouselElement)

class TestimonialsElement extends CarouselElement {
  constructor() {
    super()

    Motion.inView(this, () => {
      setTimeout(() => this.update(), 600)
    })
  }

  update() {
    if (this.carousel) this.carousel.select(0)
  }
}
customElements.define('testimonials-element', TestimonialsElement)

class MotionList extends HTMLElement {
  constructor() {
    super()

    this.isMobile = theme.config.mqlSmall || theme.config.isTouch
    this.motionReduced = theme.config.motionReduced || this.hasAttribute('motion-reduced')

    if (this.motionReduced || this.initialized) return

    this.unload()
    Motion.inView(this, this.load.bind(this))
  }

  get items() {
    return Array.from(this.querySelectorAll('.card'))
  }

  get itemsToShow() {
    return Array.from(this.querySelectorAll('.card:not([style])'))
  }

  get initialized() {
    return this.hasAttribute('initialized')
  }

  getAnimationParams() {
    return {
      distance: this.isMobile ? 30 : 50,
      duration: this.motionReduced ? 0.001 : this.isMobile ? 0.3 : 0.5,
      staggerDelay: this.motionReduced ? 0 : this.isMobile ? 0.05 : 0.1
    }
  }

  getVisibleItems(items) {
    const viewportHeight = window.innerHeight + window.scrollY
    const visible = []
    const invisible = []

    items.forEach((item) => {
      const rect = item.getBoundingClientRect()
      const isVisible = rect.top < viewportHeight && rect.bottom > 0

      if (isVisible) {
        visible.push(item)
      } else {
        invisible.push(item)
      }
    })

    return { visible, invisible }
  }

  unload() {
    const { distance } = this.getAnimationParams()
    const visibleItems = this.items

    Motion.animate(visibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration: 0 })
  }

  async load() {
    const { distance, duration, staggerDelay } = this.getAnimationParams()
    const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(this.items)

    await Motion.animate(
      visibleItems,
      { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
      { duration: duration, delay: Motion.stagger(staggerDelay) }
    ).finished
    Motion.animate(inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration })

    this.items.forEach((item) => item.style.removeProperty('transform'))
    this.setAttribute('initialized', '')
  }

  async reload() {
    const { distance, duration, staggerDelay } = this.getAnimationParams()
    const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(this.itemsToShow)

    await Motion.animate(
      visibleItems,
      { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
      { duration: duration, delay: Motion.stagger(staggerDelay) }
    ).finished
    Motion.animate(inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration })

    this.items.forEach((item) => item.style.removeProperty('transform'))
  }
}
customElements.define('motion-list', MotionList)

class LazyBackground extends HTMLElement {
  constructor() {
    super()

    this.init()
  }

  get image() {
    const style = window.getComputedStyle(this)
    return style.backgroundImage ? style.backgroundImage.slice(5, -2).replace('width=1', 'width=720') : false
  }

  async init() {
    if (!this.image) return

    const img = document.createElement('img')
    img.src = this.image
    img.style.visibility = 'hidden'
    img.style.position = 'absolute'

    await theme.utils.imageLoaded(img)
    this.style.backgroundImage = `url(${this.image})`
    img.remove()
  }
}
customElements.define('lazy-background', LazyBackground)

class MenuToggle extends MagnetButton {
  constructor() {
    super()

    this.addEventListener('click', this.onClick)
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true'
  }

  onClick() {
    this.setAttribute('aria-expanded', this.expanded ? 'false' : 'true')
    if (this.controlledElement) this.controlledElement.classList.toggle('active')
  }
}
customElements.define('menu-toggle', MenuToggle, { extends: 'button' })

class ScrollShadow extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this))
  }

  get template() {
    return this.querySelector('template')
  }

  init() {
    this.attachShadow({ mode: 'open' })
    this.shadowRoot.appendChild(this.template.content.cloneNode(true))
    this.shadowRoot.addEventListener('slotchange', () => this.load())
    this.updater = new theme.scrollShadow.Updater(this.shadowRoot.children[1])

    this.load()
  }

  load() {
    this.updater.on(this.children[0])
  }

  disconnectedCallback() {
    this.updater?.on()
  }
}
customElements.define('scroll-shadow', ScrollShadow)

class CustomSelect extends HTMLSelectElement {
  constructor() {
    super()

    this.onChange()
    this.addEventListener('change', this.onChange)
  }

  onChange() {
    this.value !== '' || this.selectedIndex === -1 ? this.setAttribute('selected', '') : this.removeAttribute('selected')
  }
}
customElements.define('custom-select', CustomSelect, { extends: 'select' })

class PreviousButton extends HoverButton {
  constructor() {
    super()

    this.load()
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  load() {
    this.onClickListener = this.onClick.bind(this)
    this.addEventListener('click', this.onClickListener)

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this)
      this.controlledElement.addEventListener('slider:previousStatus', this.updateStatusListener)
    }
  }

  unload() {
    this.removeEventListener('click', this.onClickListener)
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:previousStatus', this.updateStatusListener)
    }
  }

  onClick() {
    ;(this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:previous', { bubbles: true, cancelable: true }))
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
        this.hidden = true
        break

      case 'disabled':
        this.disabled = true
        break

      default:
        this.hidden = false
        this.disabled = false
    }
  }
}
customElements.define('previous-button', PreviousButton, { extends: 'button' })

class NextButton extends HoverButton {
  constructor() {
    super()

    this.load()
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  load() {
    this.onClickListener = this.onClick.bind(this)
    this.addEventListener('click', this.onClickListener)

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this)
      this.controlledElement.addEventListener('slider:nextStatus', this.updateStatusListener)
    }
  }

  unload() {
    this.removeEventListener('click', this.onClickListener)
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:nextStatus', this.updateStatusListener)
    }
  }

  onClick() {
    ;(this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:next', { bubbles: true, cancelable: true }))
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
        this.hidden = true
        break

      case 'disabled':
        this.disabled = true
        break

      default:
        this.hidden = false
        this.disabled = false
    }
  }
}
customElements.define('next-button', NextButton, { extends: 'button' })

class SliderElement extends HTMLElement {
  constructor() {
    super()

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' })
  }

  get looping() {
    return false
  }

  get items() {
    return (this._items =
      this._items || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children))
  }

  get itemsToShow() {
    return Array.from(this.items).filter((element) => element.clientWidth > 0)
  }

  get itemOffset() {
    if (theme.config.rtl) {
      return this.itemsToShow.length > 1 ? this.itemsToShow[0].offsetLeft - this.itemsToShow[1].offsetLeft : 0
    }

    return this.itemsToShow.length > 1 ? this.itemsToShow[1].offsetLeft - this.itemsToShow[0].offsetLeft : 0
  }

  get perPage() {
    let elementWidth = this.clientWidth
    const mql = window.matchMedia('screen and (min-width: 1280px)')
    if (mql.matches) {
      const styles = window.getComputedStyle(this)
      elementWidth = this.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight)
    }

    return Math.floor(elementWidth / this.itemOffset)
  }

  get isScrollable() {
    const style = window.getComputedStyle(this)
    const hasScrollableOverflow =
      style.overflow === 'scroll' ||
      style.overflow === 'auto' ||
      style.overflowY === 'scroll' ||
      style.overflowY === 'auto' ||
      style.overflowX === 'scroll' ||
      style.overflowX === 'auto'
    const hasScrollableContent = this.scrollHeight > this.clientHeight || this.scrollWidth > this.clientWidth

    return hasScrollableOverflow && hasScrollableContent
  }

  reset() {
    this._items = Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children)
  }

  init() {
    this.hasPendingOnScroll = false
    this.currentPage = 1
    this.updateButtons()
    this.updateTabindex()

    this.addEventListener('scroll', theme.utils.debounce(this.update.bind(this), 50))
    this.addEventListener('scrollend', this.scrollend)
    this.addEventListener('slider:previous', this.previous)
    this.addEventListener('slider:next', this.next)
    document.addEventListener('matchSmall', this.updateTabindex.bind(this))
    document.addEventListener('unmatchSmall', this.updateTabindex.bind(this))

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => event.target.scrollIntoView({ behavior: 'smooth' }))
    }
  }

  previous() {
    this.scrollPosition = this.scrollLeft - this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1)
    this.scrollToPosition(this.scrollPosition)
  }

  next() {
    this.scrollPosition = this.scrollLeft + this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1)
    this.scrollToPosition(this.scrollPosition)
  }

  select(selectedIndex, immediate = false) {
    this.scrollPosition = this.scrollLeft - (this.currentPage - selectedIndex) * this.itemOffset * (theme.config.rtl ? -1 : 1)
    this.scrollToPosition(this.scrollPosition, immediate)
  }

  selected(selectedIndex) {
    return this.itemsToShow[selectedIndex]
  }

  scrollend() {
    this.hasPendingOnScroll = false
    this.dispatchEventHandler()
  }

  update() {
    if (window.onscrollend === void 0) {
      clearTimeout(this.scrollendTimeout)
    }

    const previousPage = this.currentPage
    this.currentPage = Math.round(Math.abs(this.scrollLeft) / this.itemOffset) + 1

    if (this.currentPage !== previousPage) {
      if (!this.hasPendingOnScroll) {
        this.dispatchEventHandler()
      }

      this.itemsToShow.forEach((sliderItem, index) => {
        sliderItem.classList.toggle('selected', index + 1 === this.currentPage)
      })
    }

    if (window.onscrollend === void 0) {
      this.scrollendTimeout = setTimeout(() => {
        this.dispatchEvent(new CustomEvent('scrollend', { bubbles: true, composed: true }))
      }, 75)
    }

    if (this.looping) return

    this.updateButtons()
  }

  updateButtons() {
    let previousDisabled = this.currentPage === 1
    let nextDisabled = this.currentPage === this.itemsToShow.length

    if (this.perPage > 1) {
      previousDisabled = previousDisabled || (this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[0]) && this.scrollLeft === 0)
      nextDisabled = nextDisabled || (this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[this.itemsToShow.length - 1]))
    }

    this.dispatchEvent(
      new CustomEvent('slider:previousStatus', {
        bubbles: true,
        detail: {
          status: previousDisabled ? (nextDisabled ? 'hidden' : 'disabled') : 'visible'
        }
      })
    )
    this.dispatchEvent(
      new CustomEvent('slider:nextStatus', {
        bubbles: true,
        detail: {
          status: nextDisabled ? (previousDisabled ? 'hidden' : 'disabled') : 'visible'
        }
      })
    )
  }

  updateTabindex() {
    this.removeAttribute('tabindex')

    if (this.isScrollable) {
      this.setAttribute('tabindex', '0')
    }
  }

  isVisible(element, offset = 0) {
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft

    /*
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    const offsetLeft = element.getBoundingClientRect().left;
    return offsetLeft + element.clientWidth <= lastVisibleSlide && offsetLeft >= this.scrollLeft;
    */
  }

  scrollToPosition(position, immediate = false) {
    this.hasPendingOnScroll = !immediate

    this.scrollTo({
      left: position,
      behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth'
    })
  }

  dispatchEventHandler() {
    this.dispatchEvent(
      new CustomEvent('slider:change', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.itemsToShow[this.currentPage - 1]
        }
      })
    )
  }
}
customElements.define('slider-element', SliderElement)

class SliderDots extends HTMLElement {
  constructor() {
    super()

    new theme.initWhenVisible(this.init.bind(this))
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null
  }

  get items() {
    return (this._items = this._items || Array.from(this.children))
  }

  get itemsToShow() {
    return Array.from(this.items).filter((element) => element.clientWidth > 0)
  }

  init() {
    if (this.initialized) return
    this.initialized = true

    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:change', this.onChange.bind(this))

      this.items.forEach((item) => {
        item.addEventListener('click', this.onButtonClick.bind(this))
      })
    }
  }

  reset() {
    this._items = Array.from(this.children)
  }

  onChange(event) {
    this.transitionTo(parseInt(event.detail.currentPage) - 1)

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(event.detail.currentPage) ? 'true' : 'false')
    })
  }

  transitionTo(selectedIndex, immediate = false) {
    if (this.itemsToShow[selectedIndex]) {
      const scrollElement = this.hasAttribute('align-selected') ? this.closest(this.getAttribute('align-selected')) : this
      scrollElement?.scrollTo({
        left: this.itemsToShow[selectedIndex].offsetLeft - scrollElement.clientWidth / 2 + this.itemsToShow[selectedIndex].clientWidth / 2,
        top: this.itemsToShow[selectedIndex].offsetTop - scrollElement.clientHeight / 2 - this.itemsToShow[selectedIndex].clientHeight / 2,
        behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth'
      })
    }
  }

  onButtonClick(event) {
    event.preventDefault()
    const target = event.currentTarget

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', item.getAttribute('data-index') === target.getAttribute('data-index') ? 'true' : 'false')
    })

    this.controlledElement.select(parseInt(target.getAttribute('data-index')))
  }
}
customElements.define('slider-dots', SliderDots)

class ProgressBar extends HTMLElement {
  constructor() {
    super()

    if (this.hasAttribute('style')) return

    Motion.inView(this, this.init.bind(this))
  }

  init() {
    this.style.setProperty('--progress', `${(parseInt(this.getAttribute('data-value')) * 0.75 * 100) / parseInt(this.getAttribute('data-max'))}%`)
  }
}
customElements.define('progress-bar', ProgressBar)
class XModal extends ModalElement {
  constructor() {
    super()
  }

  get shouldLock() {
    return true
  }

  get shouldAppendToBody() {
    return true
  }
}
customElements.define('x-modal', XModal)

function getChatWidgetRoot() {
  const launcher = document.getElementById('launcher')
  if (!launcher) return null

  let node = launcher
  while (node.parentElement && node.parentElement !== document.body) {
    node = node.parentElement
  }
  return node
}

document.addEventListener('drawer:afterShow', (event) => {
  if (!event.target.matches('cart-drawer')) return

  const widgetRoot = getChatWidgetRoot()
  if (!widgetRoot) return

  widgetRoot.dataset.chatWidgetDisplay = widgetRoot.style.display
  widgetRoot.style.display = 'none'
})

document.addEventListener('drawer:afterHide', (event) => {
  if (!event.target.matches('cart-drawer')) return

  const widgetRoot = getChatWidgetRoot()
  if (!widgetRoot || widgetRoot.dataset.chatWidgetDisplay === undefined) return

  widgetRoot.style.display = widgetRoot.dataset.chatWidgetDisplay
  delete widgetRoot.dataset.chatWidgetDisplay
})
